import React from 'react';
import { useTranslation } from 'react-i18next';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import { buildWfTasksPaginationItems } from '@/pages/ipe/workbench/utils/wfTasksPaginationUtils';

export const WF_TASKS_PAGE_SIZE_OPTIONS = [
  { value: 20 },
  { value: 50 },
  { value: 200 },
  { value: 500 },
];

export const VIDEO_SPACE_PAGE_SIZE_OPTIONS = [
  { value: 20 },
  { value: 30 },
  { value: 50 },
];

export const LISTING_HUB_PAGE_SIZE_OPTIONS = [
  { value: 10 },
  ...WF_TASKS_PAGE_SIZE_OPTIONS,
];

function PaginationControls({
  safeCurrentPage,
  safeTotalPages,
  items,
  onPageChange,
  prevLabel = '‹',
  nextLabel = '›',
  prevAria,
  nextAria,
}) {
  const { t } = useTranslation('workbench');
  return (
    <div className="wf-tasks-pagination__controls">
      <button
        type="button"
        className="wf-tasks-pagination__nav"
        onClick={() => onPageChange(safeCurrentPage - 1)}
        disabled={safeCurrentPage <= 1}
        aria-label={prevAria}
      >
        {prevLabel}
      </button>

      <div className="wf-tasks-pagination__pages">
        {items.map((item) => {
          if (typeof item === 'string') {
            return (
              <span key={item} className="wf-tasks-pagination__ellipsis" aria-hidden="true">
                ...
              </span>
            );
          }

          return (
            <button
              key={item}
              type="button"
              className={`wf-tasks-pagination__page${safeCurrentPage === item ? ' wf-tasks-pagination__page--active' : ''}`}
              onClick={() => onPageChange(item)}
              aria-label={t('workflowHub.pagerPageAria', { page: item })}
              aria-current={safeCurrentPage === item ? 'page' : undefined}
            >
              {item}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="wf-tasks-pagination__nav"
        onClick={() => onPageChange(safeCurrentPage + 1)}
        disabled={safeCurrentPage >= safeTotalPages}
        aria-label={nextAria}
      >
        {nextLabel}
      </button>
    </div>
  );
}

function PageSizeSelect({
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  showPageSize,
  selectId = 'wf-tasks-page-size-select',
  sizeAria,
  perPageLabel,
}) {
  if (!showPageSize) return null;

  const handlePageSizeChange = (nextValue) => {
    const nextSize = parseInt(nextValue, 10);
    if (!Number.isFinite(nextSize) || nextSize <= 0) return;
    onPageSizeChange?.(nextSize);
  };

  return (
    <div className="wf-tasks-pagination__page-size">
      <IpeToolbarSelect
        id={selectId}
        className="wf-tasks-pagination__page-size-select"
        value={pageSize}
        onChange={handlePageSizeChange}
        options={pageSizeOptions.map((option) => ({
          ...option,
          label: perPageLabel(option.value),
        }))}
        ariaLabel={sizeAria}
        variant="light"
        clearable={false}
        disabled={!onPageSizeChange}
        width={124}
        menuPortal
      />
    </div>
  );
}

function JumpToPageInput({ currentPage, totalPages, onPageChange, jumpLabel, pageLabel, jumpAria }) {
  const [draft, setDraft] = React.useState(String(currentPage));

  React.useEffect(() => {
    setDraft(String(currentPage));
  }, [currentPage]);

  const commit = () => {
    const next = parseInt(String(draft || '').trim(), 10);
    if (!Number.isFinite(next) || next < 1) {
      setDraft(String(currentPage));
      return;
    }
    const safe = Math.min(Math.max(1, next), Math.max(1, totalPages));
    setDraft(String(safe));
    if (safe !== currentPage) onPageChange?.(safe);
  };

  return (
    <span className="wf-tasks-pagination__jump">
      {jumpLabel}
      <input
        type="text"
        inputMode="numeric"
        className="wf-tasks-pagination__jump-input"
        value={draft}
        aria-label={jumpAria}
        onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
        onBlur={commit}
      />
      {pageLabel}
    </span>
  );
}

/** 素材采集 · 采集详情分页（默认 50 条/页） */
export const SOURCING_DETAIL_PAGE_SIZE_OPTIONS = [
  { value: 50 },
  { value: 100 },
  { value: 200 },
  { value: 500 },
];

/** 与工作流任务列表一致的分页；variant="element" 对齐「按模板导出」分页风格 */
export default function WfTasksPagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize = 20,
  onPageSizeChange,
  pageSizeOptions = WF_TASKS_PAGE_SIZE_OPTIONS,
  showPageSize = true,
  totalItems,
  showTotal = true,
  totalUnit: _totalUnit = '',
  showJumpToPage = false,
  variant = 'default',
  className = '',
  ariaLabel,
}) {
  const { t } = useTranslation('workbench');
  const pagerAria = ariaLabel || t('workflowHub.pagerAria');
  const perPageLabel = (count) => t('workflowHub.pagerPerPage', { count });
  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const items = buildWfTasksPaginationItems(safeCurrentPage, safeTotalPages);
  const isElementVariant = variant === 'element';

  if (isElementVariant) {
    return (
      <nav
        className={`wf-tasks-pagination wf-tasks-pagination--element${className ? ` ${className}` : ''}`}
        aria-label={pagerAria}
      >
        {showTotal && typeof totalItems === 'number' ? (
          <span className="wf-tasks-pagination__total">{t('workflowHub.pagerTotal', { count: totalItems })}</span>
        ) : null}

        <PaginationControls
          safeCurrentPage={safeCurrentPage}
          safeTotalPages={safeTotalPages}
          items={items}
          onPageChange={onPageChange}
          prevLabel={t('workflowHub.pagerPrev')}
          nextLabel={t('workflowHub.pagerNext')}
          prevAria={t('workflowHub.pagerPrev')}
          nextAria={t('workflowHub.pagerNext')}
        />

        {showJumpToPage && onPageChange ? (
          <JumpToPageInput
            currentPage={safeCurrentPage}
            totalPages={safeTotalPages}
            onPageChange={onPageChange}
            jumpLabel={t('workflowHub.pagerJump')}
            pageLabel={t('workflowHub.pagerPage')}
            jumpAria={t('workflowHub.pagerJumpAria')}
          />
        ) : null}

        <PageSizeSelect
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
          showPageSize={showPageSize}
          selectId="wf-tasks-page-size-select-element"
          sizeAria={t('workflowHub.pagerSizeAria')}
          perPageLabel={perPageLabel}
        />
      </nav>
    );
  }

  return (
    <nav className={`wf-tasks-pagination${className ? ` ${className}` : ''}`} aria-label={pagerAria}>
      {showPageSize ? (
        <PageSizeSelect
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
          showPageSize={showPageSize}
          sizeAria={t('workflowHub.pagerSizeAria')}
          perPageLabel={perPageLabel}
        />
      ) : null}

      {showTotal && typeof totalItems === 'number' ? (
        <span className="wf-tasks-pagination__total">{t('workflowHub.pagerTotal', { count: totalItems })}</span>
      ) : !showPageSize ? (
        <span className="wf-tasks-pagination__spacer" aria-hidden="true" />
      ) : null}

      <PaginationControls
        safeCurrentPage={safeCurrentPage}
        safeTotalPages={safeTotalPages}
        items={items}
        onPageChange={onPageChange}
        prevAria={t('workflowHub.pagerPrev')}
        nextAria={t('workflowHub.pagerNext')}
      />
    </nav>
  );
}
